/*
  Nature Lane Beauty — cart Subscribe & Save switch and delivery interval.

  Switches a cart line between a one-time purchase and any Subscribe & Save
  interval, from the cart, without a page load. Dawn has no plumbing for this:
  its cart JS only ever changes quantity.

  Markup is snippets/nlb-cart-plan-select.liquid, which ships `hidden`. This
  script is what makes the control work, so this script is what reveals it.

  Two controls, one value. The switch decides whether there is a selling plan at
  all; the interval select decides which one. Either changing sends the same
  /cart/change.js call — see planId().

  ── Four things the Ajax API requires, each of which fails quietly ──────────
  Documented at https://shopify.dev/docs/api/ajax/reference/cart, and each one
  is commented at the line that satisfies it below:

    1. `quantity` must be sent. An unspecified quantity is assumed to be 1.
    2. The line must be addressed by `line`. When `selling_plan` is present the
       API accepts no other identifier.
    3. A line item key is not stable across a plan change, and a line that
       becomes identical to another is merged into it. Re-render, never patch.
    4. One-time is `selling_plan: null` — the inverse of /cart/add, where an
       empty value is rejected and the field has to be absent instead.

  ── And one thing this codebase requires ────────────────────────────────────
  See the comment on stopPropagation in onChange. It is the first line of the
  handler for a reason.
*/
(function () {
  'use strict';

  class NlbPlanSelect extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-nlb-plan-toggle]');
      this.select = this.querySelector('[data-nlb-plan-select]');
      this.offLabel = this.querySelector('[data-nlb-plan-off]');
      this.onWrap = this.querySelector('[data-nlb-plan-on]');
      this.note = this.querySelector('[data-nlb-plan-note]');
      if (!this.toggle || !this.select || this.bound) return;
      this.bound = true;

      this.previousChecked = this.toggle.checked;
      this.previousPlan = this.select.value;

      this.row = this.closest('.cart-item');
      this.drawer = this.closest('cart-drawer');

      // The static plan name is the no-JS fallback. With the switch live it
      // would only say the same thing twice.
      var fallbackLabel = this.row && this.row.querySelector('[data-nlb-plan-label]');
      if (fallbackLabel) fallbackLabel.hidden = true;

      // Safe to show now that something is listening to it.
      this.removeAttribute('hidden');

      // One listener on the wrapper catches both controls — which also means one
      // place where the event is stopped from reaching Dawn's cart JS.
      this.addEventListener('change', this.onChange.bind(this));
    }

    onChange(event) {
      /*
        Dawn's CartItems binds a debounced `change` listener on the whole
        container and routes whatever it catches into validateQuantity, which
        reads parseInt(target.value) and target.dataset.index. Neither of these
        controls carries an index, so resetQuantityInput would look up
        #Quantity-undefined, get null, and throw on input.value — after the
        request had already gone out. The symptom is a plan that changes and a
        cart that never updates. Stop it here, before anything else.
      */
      event.stopPropagation();

      if (this.busy) return;
      this.busy = true;

      // Swap the label for the interval select immediately. The request still
      // has to land, but the switch should not feel like it lagged the click.
      this.fromToggle = event.target === this.toggle;
      this.syncState();

      var line = Number(this.dataset.nlbPlanLine);
      this.lock(true);

      var body = JSON.stringify({
        // (2) `line`, never `id`. Required by the API here, and correct
        // regardless: the same variant can sit in the cart twice on different
        // plans, so a variant id would be ambiguous by construction.
        line: line,
        // (1) Never omitted. A line holding 2 would silently drop to 1.
        quantity: this.quantity(line),
        // (4) See planId — off is null, not an empty string and not an absent
        // field.
        selling_plan: this.planId(),
        sections: this.sections(),
        sections_url: window.location.pathname,
      });

      fetch(window.routes.cart_change_url, Object.assign({}, fetchConfig(), { body: body }))
        .then((response) => response.json())
        .then((state) => {
          if (!state || state.status || state.errors) {
            this.fail(line, state && (state.description || state.message || state.errors));
            return;
          }
          // (3) Full re-render, so merged and reindexed lines come back right.
          this.render(state);
          this.restore(line);
        })
        .catch(() => this.fail(line))
        .finally(() => {
          this.busy = false;
        });
    }

    /*
      The switch off is a one-time purchase, which is a null plan — not an empty
      string, and not an omitted field. Switch on commits to whatever the
      interval select is showing, including on the very first turn-on: Liquid
      preselects the default interval so there is always a real id there.
    */
    planId() {
      if (!this.toggle.checked) return null;
      return this.select.value ? Number(this.select.value) : null;
    }

    // The interval select stands in for the label while the subscription is on,
    // so exactly one of the two is visible at any time. The skip/cancel note
    // rides along with the on state — there is nothing to reassure anyone about
    // until there is a subscription.
    syncState() {
      var on = this.toggle.checked;
      if (this.onWrap) this.onWrap.hidden = !on;
      if (this.offLabel) this.offLabel.hidden = on;
      if (this.note) this.note.hidden = !on;
    }

    /*
      A lock, not a loading state.

      The row used to dim to 0.6 and raise a spinner for the length of the
      request. That read as the product flashing — and only the product, because
      cart__items--disabled covers the items container while the header and
      footer sit still. syncState has already moved the control by this point, so
      the change is acknowledged instantly and the price catches up when the
      response lands; 200ms of spinner on top of that was noise, not feedback.

      pointer-events still stops another row being clicked mid-flight, and the
      busy flag stops this one. The switch keeps its own `disabled` attribute for
      the case it actually means — a subscription-only variant.
    */
    lock(state) {
      var host = document.getElementById('CartDrawer-CartItems') || document.getElementById('main-cart-items');
      if (host) host.classList.toggle('nlb-cart-plan-busy', state);
    }

    quantity(line) {
      var input = document.getElementById('Drawer-quantity-' + line) || document.getElementById('Quantity-' + line);
      var value = input ? parseInt(input.value, 10) : NaN;
      return isNaN(value) || value < 1 ? 1 : value;
    }

    // cart-drawer-items and cart-items each already describe how their own
    // surface re-renders. Ask whichever one we are inside rather than keeping a
    // second copy of that list here.
    cartItems() {
      return this.drawer ? document.querySelector('cart-drawer-items') : document.querySelector('cart-items');
    }

    sections() {
      var items = this.cartItems();
      if (!items) return [];
      return items.getSectionsToRender().map(function (section) {
        return section.section;
      });
    }

    /*
      Replaces the contents of .drawer__inner — never .drawer__inner itself.

      That distinction is the entire fix for the drawer blanking on every change.
      component-cart-drawer.css parks .drawer__inner at translateX(100%) and only
      .drawer.active pulls it back to 0, over --duration-default (200ms).
      CartDrawer.renderContents replaces the whole of #CartDrawer, so the element
      carrying that transform was destroyed and rebuilt — and the fresh node
      started life at the off-screen end of the transition, sliding out and back
      every time. Dawn's own quantity path replaces this same inner HTML, which
      is why the stepper never flashed and this did.
    */
    render(state) {
      var items = this.cartItems();
      if (!items) return;

      items.getSectionsToRender().forEach(function (section) {
        var host = document.getElementById(section.id);
        var html = state.sections && state.sections[section.section];
        if (!host || !html) return;

        var source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        var target = host.querySelector(section.selector) || host;
        if (source) target.innerHTML = source.innerHTML;
      });
    }

    // The re-render destroyed the control that was focused, so put focus back on
    // its replacement — the switch if the switch was used, the select if the
    // select was. If the line merged into another one that index is gone, so
    // land on the first item rather than nowhere.
    restore(line) {
      var row = document.querySelector('[data-nlb-plan-line="' + line + '"]');
      var selector = this.fromToggle ? '[data-nlb-plan-toggle]' : '[data-nlb-plan-select]';
      var next = row && row.querySelector(selector);
      var target = next || document.querySelector('.cart-item__name');

      if (target) {
        if (this.drawer) trapFocus(this.drawer, target);
        else target.focus();
      }

      this.announce(row);
    }

    announce(row) {
      var region =
        document.getElementById('CartDrawer-LiveRegionText') || document.getElementById('cart-live-region-text');
      if (!region || !row) return;

      var toggle = row.querySelector('[data-nlb-plan-toggle]');
      var select = row.querySelector('[data-nlb-plan-select]');
      var text = '';

      if (toggle && toggle.checked && select) {
        var option = select.options[select.selectedIndex];
        text = option ? option.textContent.trim() : '';
      } else {
        // Not the switch label — that names what was just turned off, which
        // would announce "Subscribe & Save" to someone who just cancelled it.
        text = row.dataset.nlbPlanOnetime || '';
      }

      region.textContent = text;
      region.setAttribute('aria-hidden', 'false');

      setTimeout(function () {
        region.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    fail(line, message) {
      this.lock(false);

      this.toggle.checked = this.previousChecked;
      this.select.value = this.previousPlan;
      this.syncState();

      var error =
        document.getElementById('CartDrawer-LineItemError-' + line) ||
        document.getElementById('Line-item-error-' + line);
      var text = error && error.querySelector('.cart-item__error-text');
      if (text) text.textContent = message || (window.cartStrings && window.cartStrings.error) || '';
    }
  }

  // Custom elements upgrade on insertion, so re-rendered rows and theme-editor
  // reloads rebind themselves. No shopify:section:load hook needed.
  if (!customElements.get('nlb-plan-select')) {
    customElements.define('nlb-plan-select', NlbPlanSelect);
  }
})();
